import StartIcon from "@mui/icons-material/Start";
import { Box } from "@mui/material";
import { useState } from "react";
import CustomTextArea from "../../components/CustomTextArea";
import Flex from "../../components/Flex";
import Text from "../../components/Text";
import DeleteModal from "../../components/app/DeleteModal";
import StyledButtonV2 from "../../components/app/StyledButtonV2";
import { useReduceDisclosure } from "../../hooks/use-reduce-disclosure";
import { formatDateTime } from "../../utils/date-time-utility";
import { NoShowConfirmation } from "../../zoom/video/components/NoShow";

interface Props {
  patientName?: string;
  providerName?: string;
  startTime?: string;
  /** Cancel the appointment with a reason. Bar shows the reason modal. */
  onCancel: (reason: string) => Promise<void> | void;
  /** Mark patient as a no-show. Bar shows the confirm modal. */
  onNoShow: () => Promise<void> | void;
  /** Start the encounter — parent owns mutation, minimize, and navigation. */
  onStartEncounter: () => Promise<void> | void;
}

/**
 * Top action bar shown to the host once the meeting is active.
 * Mirrors the old ZoomStartEncounterBar: Cancel · No Show · Start Encounter.
 */
const HostActionBar = ({
  patientName,
  providerName,
  startTime,
  onCancel,
  onNoShow,
  onStartEncounter,
}: Props) => {
  const [reason, setReason] = useState<string>("");
  const [cancelDisclosure, noShowDisclosure] = useReduceDisclosure(2);

  const headerLabel =
    patientName && providerName
      ? `${patientName} Appointment With Provider ${providerName}`
      : "Appointment";

  return (
    <Box
      sx={{
        width: "100%",
        px: 2,
        py: 1,
        bgcolor: "background.paper",
        color: "text.primary",
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Flex justifyContent="space-between" alignItems="center" gap={2}>
        <Box
          sx={{
            minWidth: 0,
          }}
        >
          <Text fontSize="20px" noWrap>
            {headerLabel}
          </Text>
          {startTime && (
            <Text fontSize="14px" color="text.secondary">
              {formatDateTime(startTime.replace(/\.\d{4,}Z$/, ".000Z"))}
            </Text>
          )}
        </Box>

        <Flex gap={1}>
          <StyledButtonV2
            onClick={() => noShowDisclosure.open()}
            variant="outlined"
            sx={{ backgroundColor: "info.light" }}
            label="No Show"
          />

          <StyledButtonV2
            onClick={() => cancelDisclosure.open()}
            variant="outlined"
            sx={{
              backgroundColor: "error.main",
              color: "white",
              "&:hover": { color: "black" },
            }}
            label="Cancel"
          />

          <StyledButtonV2
            onClick={() => onStartEncounter()}
            startIcon={<StartIcon />}
            variant="contained"
            label="Start Encounter"
          />
        </Flex>
      </Flex>

      <DeleteModal
        disableOnSaveSnackbar
        w="450px"
        open={cancelDisclosure.opened}
        onClose={cancelDisclosure.close}
        onConfirmation={async () => {
          if (!reason.trim()) {
            return Promise.reject({
              response: {
                data: { message: "Reason is a required field" },
              },
            });
          }
          await onCancel(reason);
          setReason("");
        }}
        content={
          (
            <Box>
              <Text align="center">
                You want to reject this patient appointment request with you
              </Text>
              <Text align="center">
                Confirm if you have mentioned reason for Cancel
              </Text>
            </Box>
          ) as any
        }
        input={
          <Box
            sx={{
              width: "100%",
            }}
          >
            <CustomTextArea
              required
              label="Reason"
              value={reason}
              minRows={5}
              placeholder="Please enter the reason for the appointment cancellation"
              onChange={(e) => setReason(e.currentTarget.value)}
            />
          </Box>
        }
        confirmButtonText="Confirm"
        cancelButtonText="Cancel"
      />

      <NoShowConfirmation
        disclosureHost={noShowDisclosure}
        onCancel={() => noShowDisclosure.close()}
        onConfirm={async () => {
          await onNoShow();
          noShowDisclosure.close();
        }}
      />
    </Box>
  );
};

export default HostActionBar;
